import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoDinamikKesit, FikoDinamikKesitAyarlar } from './FikoDinamikKesit';
import { DuzlemKontrolComponent } from './duzlem-kontrol/duzlem-kontrol.component';
import { KesitAyarlarComponent } from './kesit-ayarlar/kesit-ayarlar.component';
import * as THREE from 'three';

@Component({
    selector: 'fikoyt-fiko-dinamik-kesit',
    standalone: true,
    imports: [CommonModule, FormsModule, DuzlemKontrolComponent, KesitAyarlarComponent],
    templateUrl: './fiko-dinamik-kesit.component.html',
    styleUrls: ['./fiko-dinamik-kesit.component.scss']
})
export class FikoDinamikKesitComponent implements OnDestroy {
    private _anaGosterim: any = null;

    @Input()
    public get anaGosterim(): any {
        return this._anaGosterim;
    }
    public set anaGosterim(v: any) {
        this._anaGosterim = v;

        // anaGosterim set edildiğinde başlat
        if (v && !this.dinamikKesit) {
            this.baslat(v);
        }
    }

    private _pencere: any;

    @Input()
    public get pencere(): any {
        return this._pencere;
    }

    public set pencere(v: any) {
        this._pencere = v?.instance || v;

        if (this.dialogCloseSub) {
            this.dialogCloseSub.unsubscribe();
            this.dialogCloseSub = null;
        }

        if (this._pencere && this._pencere.onDialogClose) {
            this.dialogCloseSub = this._pencere.onDialogClose.subscribe(() => {
                setTimeout(() => {
                    this.bitir();
                }, 0);
            });
        }

        // Pencere her set edildiğinde parametreleri kontrol et ve başlat
        this.initComponent();
    }

    @Input() ayarlar: any = {
        width: '420px',
        height: '90vh',
        closable: true,
        baslik: 'Dinamik Kesit'
    };
    @Input() parametreler: any = {};

    subsList: Subscription[] = [];
    haritaOlaylar: Map<string, any> = new Map();
    private dialogCloseSub: Subscription | null = null;

    // Dinamik Kesit Instance
    dinamikKesit: FikoDinamikKesit | null = null;

    // UI State
    kesitAktif: boolean = false;
    kesitAleti: string = 'Kontrol'; // 'Kontrol' | 'Ayarlar'

    constructor(
        public readonly FikoYT: FikoYTService,
        public readonly CD: ChangeDetectorRef
    ) { }

    private initComponent(): void {
        // Parametrelerden anaGosterim'i al
        if (this.parametreler?.anaGosterim) {
            this._anaGosterim = this.parametreler.anaGosterim;
        }

        // Ayarları merge et
        if (this.parametreler?.ayarlar) {
            this.ayarlar = { ...this.ayarlar, ...this.parametreler.ayarlar };
        }

        // Her pencere açılışında başlat
        if (this._anaGosterim) {
            this.baslat(this._anaGosterim);
        }
    }

    public baslat(anaGosterim?: any): void {
        // anaGosterim'i güncelle ama setter'ı tetikleme (sonsuz loop önlemek için)
        this._anaGosterim = anaGosterim || this._anaGosterim;

        // Dinamik kesit oluştur (sadece yoksa)
        if (!this.dinamikKesit && this._anaGosterim) {
            const sahne = this._anaGosterim?.sahneler?.[0];
            this.dinamikKesit = new FikoDinamikKesit(sahne, 'Dinamik Kesit');

            // Sahneye ekle
            if (sahne) {
                sahne.add(this.dinamikKesit);
            }
        }

        this.CD.detectChanges();
    }

    public bitir(): void {
        if (this.dinamikKesit) {
            this.kesitiDurdur();
            this.dinamikKesit.dispose();

            // Sahneden kaldır
            if (this.dinamikKesit.parent) {
                this.dinamikKesit.parent.remove(this.dinamikKesit);
            }

            this.dinamikKesit = null;
        }
    }

    /**
     * Kesiti başlat/durdur
     */
    public kesitiToggle(): void {
        if (this.kesitAktif) {
            this.kesitiDurdur();
        } else {
            this.kesitiBaslat();
        }
    }

    /**
     * Kesiti başlat
     */
    public kesitiBaslat(): void {
        if (!this.dinamikKesit || this.kesitAktif) {
            return;
        }

        this.dinamikKesit.kesitiBaslat();
        this.kesitAktif = true;

        if (this.anaGosterim) {
            this.anaGosterim.ciz();
        }

        this.CD.detectChanges();
    }

    /**
     * Kesiti durdur
     */
    public kesitiDurdur(): void {
        if (!this.dinamikKesit || !this.kesitAktif) {
            return;
        }

        this.dinamikKesit.kesitiDurdur();
        this.kesitAktif = false;

        if (this.anaGosterim) {
            this.anaGosterim.ciz();
        }

        this.CD.detectChanges();
    }

    /**
     * Ayarlar güncellendi
     */
    public ayarlarGuncellendi(yeniAyarlar: FikoDinamikKesitAyarlar): void {
        // Child component'ten gelen ayarlar
        if (this.anaGosterim) {
            this.anaGosterim.ciz();
        }
    }

    /**
     * Kapa
     */
    public kapa(): void {
        // Subscriptionları temizle
        this.subsList.forEach(sub => {
            if (sub && sub.unsubscribe) {
                sub.unsubscribe();
            }
        });

        this.haritaOlaylar.forEach((value: any, key: string) => {
            if (value && typeof value === 'function') {
                value();
            }
        });

        // Dialog close subscription'ı temizle
        if (this.dialogCloseSub) {
            this.dialogCloseSub.unsubscribe();
            this.dialogCloseSub = null;
        }

        // Dinamik kesiti temizle
        this.bitir();

        // Dialog core'ın kapa metodunu çağır
        if (this._pencere && typeof this._pencere.kapa === 'function') {
            try {
                this._pencere.kapa();
            } catch (error) {
                // Dialog kapatma hatası
            }
        }
    }

    ngOnDestroy(): void {
        this.bitir();

        this.subsList.forEach(sub => {
            if (sub && sub.unsubscribe) {
                sub.unsubscribe();
            }
        });

        this.haritaOlaylar.forEach((value: any, key: string) => {
            if (value && typeof value === 'function') {
                value();
            }
        });

        if (this.dialogCloseSub) {
            this.dialogCloseSub.unsubscribe();
            this.dialogCloseSub = null;
        }

        this.anaGosterim = null;
        this._pencere = null;
    }
}
